import { computed, toRef } from 'vue';
import { useAcl } from '@/composables';
import { useFlowItemFieldErrors } from '@/composables/useFlowDialogFieldErrors';
import { useCampaignFlowSelectOptions } from './useCampaignFlowSelectOptions';

type CampaignFlowOfferProps = {
  path: {
    offers: Array<{ id: string; weight: number; url?: string }>;
  };
  ruleIndex: number;
  pathIndex: number;
  offerIndex: number;
  offer: { id: string; weight: number; url?: string };
  offersCount: number;
  isDisabledWeight?: boolean;
  totalWeight: number;
  errorConditionPrefix?: string[];
  errorsRaw: unknown[][] | null;
  offerOptions: Offer[];
  form: {
    workspaceId: string;
    countryId?: string;
  };
};

export const useCampaignFlowOffer = (props: CampaignFlowOfferProps) => {
  const { aclCanEditItem } = useAcl();
  const { fetchOfferOptions } = useCampaignFlowSelectOptions(() => props.form);

  const errorCondition = computed(() => [
    ...(props.errorConditionPrefix ?? []),
    'rules',
    props.ruleIndex + 1,
    'paths',
    props.pathIndex + 1,
    'offers',
    props.offerIndex + 1,
  ]);

  const { generalErrorMessages, weightErrorMessages } = useFlowItemFieldErrors({
    errorsRaw: toRef(props, 'errorsRaw'),
    errorCondition,
  });

  const getItem = (id: string) => props.path.offers.find((item) => item.id === id);

  const offerUrl = computed(() => {
    const offer = getItem(props.offer.id);
    return offer?.url || '';
  });

  const checkPreviewAccess = computed(() => {
    const data = getItem(props.offer.id);

    if (!data) {
      return true;
    }

    return !aclCanEditItem({ ...data, type: 'offers' });
  });

  const offerPercentage = computed(() => {
    if (props.totalWeight === 0) {
      return 0;
    }

    return Math.round((props.offer.weight / props.totalWeight) * 100);
  });

  const onChange = (value: Record<string, unknown>) => {
    props.path.offers.splice(props.offerIndex, 1, {
      weight: props.offer.weight,
      ...value,
    } as { id: string; weight: number; url?: string });
  };

  return {
    fetchOfferOptions,
    generalErrorMessages,
    weightErrorMessages,
    offerUrl,
    checkPreviewAccess,
    offerPercentage,
    onChange,
  };
};
